import React, {useState, useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import {DataGrid} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  IconButton,
  Paper
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import {GET, PATCH} from "APIclient";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";
import {useNavigate} from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button
} from "@mui/material";

// Custom Pagination Component
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading,
}) {
  const handleFirstPage = () => onPageChange(0);
  const handlePreviousPage = () => onPageChange(Math.max(0, page - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages - 1, page + 1));
  const handleLastPage = () => onGoToLastPage();

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="caption" sx={{ color: "#666" }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor:
                  properties?.colors?.bgcolorbluethem || "#1565c0",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function JobVacancyList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 20});
  const [searchParams, setSearchParams] = useState({jobId: "", status: ""});
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);

  const fetchData = async (page, pageSize, searchParams = {}) => {
    try {
      setLoading(true);
      const params = {
        jobId: searchParams.jobId || null,
        status: searchParams.status || null,
        page,
        size: pageSize
      };

      const queryString = Object.keys(params)
        .filter(key => params[key] !== null)
        .map(key => `${key}=${encodeURIComponent(params[key])}`)
        .join("&");

      const fetchUrl = `admin/job-opening-list?${queryString}`;
      const response = await GET(fetchUrl);

      if (response.status === 200) {
        const data = response?.data?.data?.paginatedData || [];
        const totalRecords = response?.data?.data?.totalItems || 0;
        const totalPages = response?.data?.data?.totalPages || 0;

        const formattedData = data.map((item, index) => ({
          id: page * pageSize + index + 1,
          openingId: item?.openingId,
          jobId: item?.jobId,
          positionName: item?.positionName,
          experience: `${item?.minExp} - ${item?.maxExp} Yrs`,
          skills: item?.skills,
          addedOn: item?.addedOn,
          status: item?.status
        }));

        setRows(formattedData);
        setFilteredRows(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);
      } else {
        setRows([]);
        setFilteredRows([]);
        setTotalRows(0);
        setTotalPages(0);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Error fetching job data.";
      setRows([]);
      setFilteredRows([]);
      setTotalRows(0);
      setTotalPages(0);
      setSnakColor("error");
      setSnakMessage(errorMessage);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(paginationModel.page, paginationModel.pageSize, searchParams);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const onSearch = event => {
    const {name, value} = event.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async () => {
    try {
      setPaginationModel({page: 0, pageSize: paginationModel.pageSize});
      setLoading(true);
      await fetchData(0, paginationModel.pageSize, searchParams);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSnakColor("error");
      setSnakMessage("Error fetching search results");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Updated pagination handlers
  const handlePageChange = (newPage) => {
    const updatedPaginationModel = { ...paginationModel, page: newPage };
    setPaginationModel(updatedPaginationModel);
    fetchData(newPage, paginationModel.pageSize, searchParams);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const updatedPaginationModel = { ...paginationModel, page: lastPageIndex };
      setPaginationModel(updatedPaginationModel);
      await fetchData(lastPageIndex, paginationModel.pageSize, searchParams);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  const handleCloseJobOpening = async () => {
    try {
      setLoading(true);
      const url = `admin/close-job-opening/${selectedOpeningId}`;
      const response = await PATCH(url, {status: 0});
      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage(response?.data?.message || "Job opening closed successfully");
        setOpen(true);
        await fetchData(paginationModel.page, paginationModel.pageSize, searchParams);
      } else {
        setSnakColor("error");
        setSnakMessage("Failed to close job opening");
        setOpen(true);
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to close job opening.";
      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage(message);
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don't have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Conflict. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    } finally {
      setLoading(false);
      setOpenDialog(false);
      setSelectedOpeningId(null);
    }
  };

  const handleDialogOpen = openingId => {
    setSelectedOpeningId(openingId);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedOpeningId(null);
  };

  const renderStatusChip = params => {
    return (
      <Chip
        size="small"
        label={params.value === 1 ? "Open" : "Closed"}
        color={params.value === 1 ? "success" : "error"}
        sx={{
          backgroundColor: params.value === 1 ? "green" : "red",
          color: "#fff",
          fontWeight: "400"
        }}
      />
    );
  };

  const columns = [
    {field: "id", headerName: "#", width: 30, headerAlign: "center", align: "center"},
    {
      field: "jobId",
      headerName: "Job ID",
      width: 150,
      headerAlign: "left",
      align: "left",
      renderCell: params => (
        <Tooltip title={params.value || "N/A"} arrow>
          <span>{params.value || "N/A"}</span>
        </Tooltip>
      )
    },
    {
      field: "positionName",
      headerName: "Position Name",
      width: 200,
      headerAlign: "left",
      align: "left",
      renderCell: params => (
        <Tooltip title={params.value || "N/A"} arrow>
          <span>{params.value || "N/A"}</span>
        </Tooltip>
      )
    },
    {
      field: "experience",
      headerName: "Experience",
      width: 120,
      headerAlign: "left",
      align: "left"
    },
    {
      field: "skills",
      headerName: "Skills",
      width: 200,
      headerAlign: "left",
      align: "left",
      renderCell: params => (
        <Tooltip title={params.value || "N/A"} arrow>
          <span>{params.value || "N/A"}</span>
        </Tooltip>
      )
    },
    {
      field: "addedOn",
      headerName: "Added On",
      width: 120,
      headerAlign: "left",
      align: "left",
      renderCell: params => {
        const formattedDate = formatDate(params.value);
        return formattedDate || "N/A";
      }
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      headerAlign: "left",
      align: "left",
      renderCell: renderStatusChip
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({row}) => [
        <Tooltip title="Edit Job Opening" key="edit">
          <IconButton
            color="primary"
            onClick={() => navigate(`/addJobVacancy?openingId=${row.openingId}`)}
            disabled={row.status !== 1}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Close Job Opening" arrow key="close">
          <IconButton
            color="error"
            onClick={() => handleDialogOpen(row.openingId)}
            disabled={row.status !== 1}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const handleNullValues = data => {
    return data && data.length
      ? data.map(row => ({
          ...row,
          jobId: row.jobId ?? "N/A",
          positionName: row.positionName ?? "N/A",
          experience: row.experience ?? "N/A",
          skills: row.skills ?? "N/A",
          addedOn: row.addedOn ?? "N/A"
        }))
      : [];
  };

  const cleanedRows = handleNullValues(filteredRows);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox container pt={2} pb={2} px={3}>
              <MDBox sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 3}}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Job Vacancy List
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 3"}}
                  value={searchParams.jobId}
                  onChange={onSearch}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  name="jobId"
                  label="Job ID"
                  variant="outlined"
                />
                <FormControl variant="outlined" sx={{m: 0, width: "20ch", gridColumn: "span 3"}}>
                  <InputLabel id="status">Status</InputLabel>
                  <Select
                    labelId="status"
                    id="status"
                    name="status"
                    value={searchParams.status}
                    onChange={onSearch}
                    onKeyDown={event => {
                      if (event.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    label="Status"
                    sx={{minWidth: 150, p: 1.5}}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="1">Open</MenuItem>
                    <MenuItem value="0">Closed</MenuItem>
                  </Select>
                </FormControl>
                <MDButton
                  sx={{
                    gridColumn: "span 2",
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem
                  }}
                  variant="contained"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
              </MDBox>
            </MDBox>

            {/* DataGrid with Custom Pagination */}
            <MDBox>
              <Paper>
                <DataGrid
                  rows={cleanedRows}
                  columns={columns}
                  // rowHeight={35}
                  getRowId={row => row.openingId}
                  columnVisibilityModel={{
                    openingId: false
                  }}
                  sx={{
                    height: "450px",
                    fontWeight:400,
                    fontSize: "14px",
                    fontFamily: properties.fontFamily,
                    "& .MuiDataGrid-scrollbar": {
                    width: "10px !important",
                    },
                    "& .actions": {
                      color: "text.secondary"
                    },
                    "& .textPrimary": {
                      color: "text.primary"
                    }
                  }}
                  aria-label="job openings table"
                  paginationMode="server"
                  rowCount={totalRows}
                  pageSizeOptions={[20]}
                  paginationModel={paginationModel}
                  loading={loading}
                  hideFooter
                />
                <CustomPagination
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              </Paper>
            </MDBox>

            {/* Confirmation Dialog */}
            <Dialog
              open={openDialog}
              onClose={handleDialogClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                id="alert-dialog-title"
                sx={{fontSize: "16px", fontFamily: properties.fontFamily}}
              >
                Confirm Close Job Opening
              </DialogTitle>
              <DialogContent>
                <DialogContentText
                  id="alert-dialog-description"
                  sx={{fontSize: "14px", fontFamily: properties.fontFamily}}
                >
                  Are you sure you want to close this job opening?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleDialogClose}
                  color="primary"
                  sx={{fontFamily: properties.fontFamily}}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCloseJobOpening}
                  color="primary"
                  sx={{fontFamily: properties.fontFamily}}
                  autoFocus
                  disabled={loading}
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
              open={open}
              anchorOrigin={{vertical: "top", horizontal: "right"}}
              autoHideDuration={6000}
              onClose={handleClose}
            >
              <Alert
                onClose={handleClose}
                severity={snakColor === "success" ? "success" : "error"}
                variant="filled"
                sx={{width: "100%", color: "#fff"}}
              >
                {snakMessage}
              </Alert>
            </Snackbar>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}